/**
 * Médias d'une actualité : vidéo (YouTube, Vimeo, fichier MP4) ou podcast (Ausha).
 *
 * L'API stocke une `url` par média ; c'est ICI qu'on décide comment la rendre.
 * Le marchand colle un lien, le serveur le reconnaît (`/publications/media-preview`)
 * et normalise ce qu'il faut (le smartlink Ausha devient l'URL du lecteur) ;
 * le client n'a plus qu'à choisir entre iframe et balise <video>.
 */
import React from 'react';
import './PublicationMedia.css';

export type MediaKind = 'youtube' | 'vimeo' | 'ausha' | 'file' | 'audio' | 'link';

export interface ParsedMedia {
  kind: MediaKind;
  /** URL à mettre dans un <iframe> (youtube, vimeo, ausha) */
  embedUrl?: string;
  /** URL du fichier pour une balise <video> ou <audio> */
  fileUrl?: string;
  /** Vignette déductible de l'URL seule (YouTube) */
  thumbnail?: string;
}

const YOUTUBE_ID =
  /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
const VIMEO_ID = /vimeo\.com\/(?:video\/)?(\d+)/;

export function parseMediaUrl(url: string): ParsedMedia {
  const yt = url.match(YOUTUBE_ID);
  if (yt) {
    return {
      kind: 'youtube',
      embedUrl: `https://www.youtube-nocookie.com/embed/${yt[1]}`,
      thumbnail: `https://i.ytimg.com/vi/${yt[1]}/hqdefault.jpg`,
    };
  }
  const vimeo = url.match(VIMEO_ID);
  if (vimeo) {
    return { kind: 'vimeo', embedUrl: `https://player.vimeo.com/video/${vimeo[1]}` };
  }
  if (/^https:\/\/player\.ausha\.co\//.test(url)) {
    return { kind: 'ausha', embedUrl: url };
  }
  if (/\.(mp4|webm|m4v|mov)(\?.*)?$/i.test(url)) {
    return { kind: 'file', fileUrl: url };
  }
  if (/\.(mp3|m4a|aac|ogg|oga|wav|flac)(\?.*)?$/i.test(url)) {
    return { kind: 'audio', fileUrl: url };
  }
  return { kind: 'link' };
}

/** Nom lisible d'un fichier audio/vidéo (pour la légende et le lien de téléchargement). */
export function mediaFileName(url: string): string {
  try {
    const last = new URL(url).pathname.split('/').pop() || '';
    return decodeURIComponent(last);
  } catch {
    return url.split('/').pop() || url;
  }
}

export interface PublicationVideoLike {
  url: string;
  thumbnail_url?: string;
  caption?: string;
}

/** Vignette à afficher sur une carte pour ce média (si on en connaît une). */
export function mediaThumbnail(video: PublicationVideoLike): string | undefined {
  return video.thumbnail_url || parseMediaUrl(video.url).thumbnail;
}

/** « Vidéo » ou « Podcast », pour les libellés. */
export function mediaLabel(video: PublicationVideoLike): string {
  const kind = parseMediaUrl(video.url).kind;
  return kind === 'ausha' || kind === 'audio' ? 'Podcast' : 'Vidéo';
}

export function mediaIcon(video: PublicationVideoLike): string {
  const kind = parseMediaUrl(video.url).kind;
  return kind === 'ausha' || kind === 'audio' ? '🎧' : '🎬';
}

interface PublicationMediaProps {
  video: PublicationVideoLike;
  title?: string;
}

const PublicationMedia: React.FC<PublicationMediaProps> = ({ video, title }) => {
  const media = parseMediaUrl(video.url);
  const label = video.caption || title || mediaLabel(video);

  return (
    <figure className="pub-media">
      {media.kind === 'youtube' || media.kind === 'vimeo' ? (
        <div className="pub-media-frame">
          <iframe
            src={media.embedUrl}
            title={label}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      ) : media.kind === 'ausha' ? (
        <iframe
          className="pub-media-audio"
          src={media.embedUrl}
          title={label}
          loading="lazy"
          allow="autoplay; clipboard-write"
        />
      ) : media.kind === 'audio' ? (
        <div className="pub-media-audio-file">
          <audio controls preload="metadata" src={media.fileUrl}>
            Votre navigateur ne lit pas ce fichier audio.
          </audio>
          {/* Lien direct vers le fichier (copie conservée sur notre stockage) */}
          <a className="pub-media-download" href={media.fileUrl} target="_blank" rel="noopener noreferrer">
            ⬇︎ Télécharger l'audio ({mediaFileName(media.fileUrl!)})
          </a>
        </div>
      ) : media.kind === 'file' ? (
        <video
          className="pub-media-video"
          controls
          playsInline
          preload="metadata"
          poster={video.thumbnail_url || undefined}
          src={media.fileUrl}
        >
          Votre navigateur ne lit pas cette vidéo.{' '}
          <a href={media.fileUrl} target="_blank" rel="noopener noreferrer">
            La télécharger
          </a>
        </video>
      ) : (
        <a className="pub-media-link" href={video.url} target="_blank" rel="noopener noreferrer">
          🎬 Voir la vidéo
        </a>
      )}
      {video.caption && <figcaption className="pub-media-caption">{video.caption}</figcaption>}
    </figure>
  );
};

export default PublicationMedia;

const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)"'\]])/g;
const IS_URL = /^https?:\/\//;

/**
 * Texte brut → texte avec liens cliquables. Le contenu d'une actualité n'est
 * pas du HTML (jamais de dangerouslySetInnerHTML) : on découpe sur les URL.
 * `split` avec un groupe capturant alterne texte / URL / texte / URL…
 */
export const LinkifiedText: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const parts = text.split(URL_RE);
  return (
    <div className={className}>
      {parts.map((part, i) =>
        IS_URL.test(part) ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="pub-text-link">
            {part}
          </a>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </div>
  );
};

/** Aperçu court sans les URL brutes (une adresse de 130 caractères n'apprend rien sur une carte). */
export function previewText(text: string, max: number): string {
  const stripped = text.replace(URL_RE, '').replace(/[ \t]+/g, ' ').replace(/\n{2,}/g, '\n').trim();
  if (stripped.length <= max) return stripped;
  return `${stripped.substring(0, max).trimEnd()}…`;
}
